/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import debounce from 'lodash/debounce';

import './ScrollToReadMore.scss';

const SCROLL_END_THRESHOLD = 2;

export function useScrollToReadMore(
    scrollElement: HTMLElement | null,
    contentElement?: HTMLElement | null,
    resetKey?: unknown
) {
    const [isVisible, setIsVisible] = useState(false);

    const check = useCallback(() => {
        if (!scrollElement) {
            setIsVisible(false);
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = scrollElement;
        const remaining = scrollHeight - clientHeight - scrollTop;
        const overflows = scrollHeight - clientHeight > SCROLL_END_THRESHOLD;

        setIsVisible(overflows && remaining > SCROLL_END_THRESHOLD);
    }, [scrollElement]);

    // Recheck when the element or its content changes
    useEffect(() => {
        check();
    }, [check, resetKey]);

    useEffect(() => {
        if (!scrollElement) return undefined;

        const handleScroll = () => check();
        const handleResize = debounce(() => check(), 100);

        scrollElement.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);

        // Content (e.g. images) can load after mount, changing the scroll height
        const observeTarget = contentElement ?? scrollElement;
        const observer = new ResizeObserver(() => check());
        observer.observe(observeTarget);

        return () => {
            scrollElement.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            handleResize.cancel();
            observer.disconnect();
        };
    }, [scrollElement, contentElement, check]);

    return isVisible;
}

interface ScrollToReadMoreProps {
    /** The scrolling container to watch. */
    scrollElement: HTMLElement | null;
    /**
     * Element to observe for size changes (e.g. images loading in). Defaults to
     * the scroll element itself.
     */
    contentElement?: HTMLElement | null;
    /** Change this to force a recheck when swapping content (page/step index). */
    resetKey?: unknown;
    label?: string;
    className?: string;
}

/**
 * A sticky "Scroll to Read More" pill that sits at the bottom of a scroll
 * container and fades out once the user reaches the end.
 *
 * Render this as a child of the scroll container (`scrollElement`) so its
 * `position: sticky` anchors to the bottom of the scrolled content.
 */
function ScrollToReadMore({
    scrollElement,
    contentElement,
    resetKey,
    label = 'Scroll to Read More',
    className,
}: ScrollToReadMoreProps) {
    const isVisible = useScrollToReadMore(scrollElement, contentElement, resetKey);

    return (
        <div
            className={cx('scroll-to-read-more', className, {
                'scroll-to-read-more--hidden': !isVisible,
            })}
            aria-hidden="true"
        >
            <div className="scroll-to-read-more__pill">{label}</div>
        </div>
    );
}

export default ScrollToReadMore;
